/**
 * Sync Service (Day-15 Module 7)
 * 
 * Comprehensive sync service with:
 * - Network state monitoring
 * - Offline queue processing
 * - Exponential backoff retry strategy
 * - Conflict resolution
 * - Real-time status tracking
 */

import api from '../api';
import {
  getAllQueuedActions,
  updateActionStatus,
  markActionSynced,
  markActionFailed,
  incrementRetryCount,
  getQueueStats,
  ACTION_STATUS,
  ACTION_TYPES,
} from './actionQueue';
import { STORES, updateInStore, getFromStore, initOfflineDB } from './storage';

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const MAX_RETRIES = 5;
const BASE_BACKOFF_MS = 1000; // 1 second
const MAX_BACKOFF_MS = 30000; // 30 seconds
const AUTO_SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SYNC_ON_RECONNECT_DELAY_MS = 1000;

// ─────────────────────────────────────────────────────────────────
// Sync Service State
// ─────────────────────────────────────────────────────────────────

class SyncService {
  constructor() {
    // Sync state
    this.isSyncing = false;
    this.isInitialized = false;
    
    // Tracking
    this.lastSyncedAt = null;
    this.pendingCount = 0;
    this.failedCount = 0;
    this.syncedCount = 0;
    this.conflictCount = 0;
    
    // Conflicts tracking
    this.conflicts = [];
    this.conflictListeners = new Set();
    
    // Network state
    this.isOnline = navigator.onLine;
    
    // Listeners
    this.listeners = new Set();
    this.networkListeners = new Set();
    
    // Timers
    this.autoSyncTimer = null;
    this.retryTimers = new Map();
    
    // Bind methods
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
  }

  // ─────────────────────────────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────────────────────────────

  async init() {
    if (this.isInitialized) return;

    try {
      // Initialize IndexedDB
      await initOfflineDB();
      
      // Setup network monitoring
      this.setupNetworkMonitoring();
      
      // Load last sync info
      const lastSync = await getFromStore(STORES.SYNC_STATE, 'lastSync');
      if (lastSync) {
        this.lastSyncedAt = lastSync.timestamp;
      }
      
      // Update counts
      await this.updateCounts();
      
      // Setup auto-sync
      this.setupAutoSync();
      
      // Initial sync if online
      if (this.isOnline) {
        setTimeout(() => this.syncAll(), 2000);
      }
      
      this.isInitialized = true;
      console.log('[SyncService] Initialized');
    } catch (error) {
      console.error('[SyncService] Init error:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Network State Monitoring
  // ─────────────────────────────────────────────────────────────────

  setupNetworkMonitoring() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Periodic network check (some browsers don't fire events reliably)
    setInterval(() => {
      const wasOnline = this.isOnline;
      this.isOnline = navigator.onLine;
      
      if (!wasOnline && this.isOnline) {
        this.handleOnline();
      } else if (wasOnline && !this.isOnline) {
        this.handleOffline();
      }
    }, 10000); // Check every 10 seconds
  }

  handleOnline() {
    console.log('[SyncService] Network: Online');
    this.isOnline = true;
    this.notifyNetworkChange({ online: true });
    
    // Start sync after delay
    setTimeout(() => {
      if (this.isOnline && !this.isSyncing) {
        this.syncAll();
      }
    }, SYNC_ON_RECONNECT_DELAY_MS);
  }

  handleOffline() {
    console.log('[SyncService] Network: Offline');
    this.isOnline = false;
    this.notifyNetworkChange({ online: false });
    
    // Clear any pending retry timers
    this.clearRetryTimers();
  }

  onNetworkChange(callback) {
    this.networkListeners.add(callback);
    // Immediately call with current state
    callback({ online: this.isOnline });
    return () => this.networkListeners.delete(callback);
  }

  notifyNetworkChange(state) {
    this.networkListeners.forEach(cb => {
      try { cb(state); } catch (e) { console.error(e); }
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // Sync Status Management
  // ─────────────────────────────────────────────────────────────────

  onSyncStatusChange(callback) {
    this.listeners.add(callback);
    // Immediately call with current state
    callback(this.getStatus());
    return () => this.listeners.delete(callback);
  }

  notifyStatusChange() {
    const status = this.getStatus();
    this.listeners.forEach(cb => {
      try { cb(status); } catch (e) { console.error(e); }
    });
    
    // Also dispatch event for components not using the service directly
    window.dispatchEvent(new CustomEvent('syncStatusChanged', { detail: status }));
  }

  getStatus() {
    return {
      syncing: this.isSyncing,
      online: this.isOnline,
      lastSyncedAt: this.lastSyncedAt,
      pendingCount: this.pendingCount,
      failedCount: this.failedCount,
      syncedCount: this.syncedCount,
      conflictCount: this.conflictCount,
      conflicts: this.conflicts,
    };
  }

  async updateCounts() {
    try {
      const stats = await getQueueStats();
      this.pendingCount = stats.pending || 0;
      this.failedCount = stats.failed || 0;
      this.syncedCount = stats.synced || 0;
    } catch (error) {
      console.error('[SyncService] Failed to update counts:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Conflict Management
  // ─────────────────────────────────────────────────────────────────

  onConflictChange(callback) {
    this.conflictListeners.add(callback);
    // Immediately call with current conflicts
    callback(this.conflicts);
    return () => this.conflictListeners.delete(callback);
  }

  notifyConflictChange() {
    this.conflictListeners.forEach(cb => {
      try { cb(this.conflicts); } catch (e) { console.error(e); }
    });
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('syncConflicts', { detail: this.conflicts }));
  }

  addConflict(conflict) {
    // Avoid duplicates
    const existing = this.conflicts.find(c => 
      c.clientActionId === conflict.clientActionId
    );
    
    if (!existing) {
      this.conflicts.push({
        ...conflict,
        timestamp: new Date().toISOString(),
      });
      this.conflictCount = this.conflicts.length;
      this.notifyConflictChange();
    }
  }

  dismissConflict(clientActionId) {
    this.conflicts = this.conflicts.filter(c => c.clientActionId !== clientActionId);
    this.conflictCount = this.conflicts.length;
    this.notifyConflictChange();
    this.notifyStatusChange();
  }

  dismissAllConflicts() {
    this.conflicts = [];
    this.conflictCount = 0;
    this.notifyConflictChange();
    this.notifyStatusChange();
  }

  getConflicts() {
    return this.conflicts;
  }

  // ─────────────────────────────────────────────────────────────────
  // Auto Sync
  // ─────────────────────────────────────────────────────────────────

  setupAutoSync() {
    // Clear existing timer
    if (this.autoSyncTimer) {
      clearInterval(this.autoSyncTimer);
    }
    
    // Setup periodic sync
    this.autoSyncTimer = setInterval(() => {
      if (this.isOnline && !this.isSyncing && this.pendingCount > 0) {
        console.log('[SyncService] Auto-sync triggered');
        this.syncAll();
      }
    }, AUTO_SYNC_INTERVAL_MS);
  }

  stopAutoSync() {
    if (this.autoSyncTimer) {
      clearInterval(this.autoSyncTimer);
      this.autoSyncTimer = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Retry Management
  // ─────────────────────────────────────────────────────────────────

  /**
   * Calculate exponential backoff delay
   */
  calculateBackoff(retryCount) {
    const delay = Math.min(
      BASE_BACKOFF_MS * Math.pow(2, retryCount),
      MAX_BACKOFF_MS
    );
    // Add jitter (±20%)
    const jitter = delay * 0.2 * (Math.random() - 0.5);
    return Math.round(delay + jitter);
  }

  /**
   * Schedule retry for a failed action
   */
  scheduleRetry(actionId, retryCount) {
    if (retryCount >= MAX_RETRIES) {
      console.log(`[SyncService] Action ${actionId} exceeded max retries`);
      return;
    }

    const delay = this.calculateBackoff(retryCount);
    console.log(`[SyncService] Scheduling retry for ${actionId} in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);

    const timer = setTimeout(async () => {
      this.retryTimers.delete(actionId);
      
      if (!this.isOnline) {
        console.log(`[SyncService] Offline, skipping retry for ${actionId}`);
        return;
      }

      await this.retryAction(actionId);
    }, delay);

    this.retryTimers.set(actionId, timer);
  }

  clearRetryTimers() {
    this.retryTimers.forEach(timer => clearTimeout(timer));
    this.retryTimers.clear();
  }

  /**
   * Retry a specific failed action
   */
  async retryAction(actionId) {
    const actions = await getAllQueuedActions();
    const action = actions.find(a => a.id === actionId);
    
    if (!action) {
      console.log(`[SyncService] Action ${actionId} not found for retry`);
      return null;
    }

    if (action.status === ACTION_STATUS.SYNCED) {
      console.log(`[SyncService] Action ${actionId} already synced`);
      return { success: true, alreadySynced: true };
    }

    // Increment retry count
    await incrementRetryCount(actionId);
    
    return await this.syncSingleAction(action);
  }

  // ─────────────────────────────────────────────────────────────────
  // Core Sync Operations
  // ─────────────────────────────────────────────────────────────────

  /**
   * Sync all pending actions using batch endpoint
   */
  async syncAll() {
    if (this.isSyncing) {
      console.log('[SyncService] Sync already in progress');
      return { skipped: true };
    }

    if (!this.isOnline) {
      console.log('[SyncService] Offline - skipping sync');
      return { offline: true };
    }

    this.isSyncing = true;
    this.notifyStatusChange();

    const results = {
      total: 0,
      synced: 0,
      failed: 0,
      duplicates: 0,
      conflicts: 0,
    };

    try {
      const actions = await getAllQueuedActions();
      
      // Get pending and retryable failed actions
      const pending = actions.filter(
        a => a.status === ACTION_STATUS.PENDING || 
             (a.status === ACTION_STATUS.FAILED && (a.retryCount || 0) < MAX_RETRIES)
      );

      results.total = pending.length;

      if (pending.length === 0) {
        console.log('[SyncService] No pending actions');
        await this.updateSyncState(results);
        return results;
      }

      // Sort by createdAt to maintain order
      pending.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      console.log(`[SyncService] Syncing ${pending.length} actions`);

      // Prepare batch request
      const batchActions = pending.map(action => ({
        clientActionId: action.id,
        actionType: action.actionType,
        payload: action.payload,
        createdAt: action.createdAt,
      }));

      // Send batch sync request
      const response = await api.post('/mobile/sync', { actions: batchActions });
      const data = response.data;

      // Process successful actions
      for (const success of (data.successActions || [])) {
        const action = pending.find(a => a.id === success.clientActionId);
        if (action) {
          await markActionSynced(action.id, success.serverId);
          results.synced++;
        }
      }

      // Process failed actions
      for (const failed of (data.failedActions || [])) {
        const action = pending.find(a => a.id === failed.clientActionId);
        if (action) {
          await markActionFailed(action.id, failed.error?.message || 'Sync failed');
          results.failed++;
          
          // Schedule retry if applicable
          const retryCount = (action.retryCount || 0) + 1;
          if (retryCount < MAX_RETRIES) {
            this.scheduleRetry(action.id, retryCount);
          }
        }
      }

      // Process conflicts
      for (const conflict of (data.conflicts || [])) {
        const action = pending.find(a => a.id === conflict.clientActionId);
        if (action) {
          const resolved = await this.resolveConflict(action, conflict);
          if (resolved) {
            results.duplicates++;
          } else {
            results.conflicts++;
            // Track unresolved conflict for UI display
            this.addConflict(conflict);
          }
        }
      }

      await this.updateSyncState(results);
      console.log('[SyncService] Sync complete:', results);

      return results;
    } catch (error) {
      console.error('[SyncService] Sync error:', error);
      
      // Fallback to single-action sync if batch fails
      if (error.response?.status === 422 || error.response?.status === 400) {
        console.log('[SyncService] Batch failed, falling back to single-action sync');
        return await this.syncAllSingle();
      }
      
      return { error: error.message };
    } finally {
      this.isSyncing = false;
      await this.updateCounts();
      this.notifyStatusChange();
    }
  }

  /**
   * Fallback: Sync actions one by one
   */
  async syncAllSingle() {
    const results = {
      total: 0,
      synced: 0,
      failed: 0,
      duplicates: 0,
    };

    try {
      const actions = await getAllQueuedActions();
      const pending = actions.filter(
        a => a.status === ACTION_STATUS.PENDING || 
             (a.status === ACTION_STATUS.FAILED && (a.retryCount || 0) < MAX_RETRIES)
      );

      results.total = pending.length;
      pending.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      for (const action of pending) {
        const result = await this.syncSingleAction(action);
        
        if (result.success) {
          results.synced++;
          if (result.duplicate) results.duplicates++;
        } else {
          results.failed++;
        }

        // Small delay between syncs
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      await this.updateSyncState(results);
      return results;
    } catch (error) {
      console.error('[SyncService] Single sync error:', error);
      return { error: error.message };
    }
  }

  /**
   * Sync a single action
   */
  async syncSingleAction(action) {
    const endpoint = this.getEndpoint(action.actionType);
    
    if (!endpoint) {
      console.warn(`[SyncService] No endpoint for action type: ${action.actionType}`);
      await markActionFailed(action.id, `Unknown action type: ${action.actionType}`);
      return { success: false, error: 'Unknown action type' };
    }

    try {
      const url = endpoint.getUrl(action);
      const body = endpoint.getBody(action);

      const config = endpoint.isMultipart 
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};

      const response = await api.post(url, body, config);
      const data = response.data?.data || response.data;

      // Check for ALREADY_SYNCED response (idempotency)
      if (data?.status === 'ALREADY_SYNCED') {
        await markActionSynced(action.id, data.existingId || data.id);
        return { success: true, serverId: data.existingId || data.id, duplicate: true };
      }

      const serverId = data?.id;
      await markActionSynced(action.id, serverId);
      
      return { success: true, serverId };
    } catch (error) {
      const errorMessage = this.getErrorMessage(error);
      
      // Check for conflict (409)
      if (error.response?.status === 409) {
        const existingId = error.response?.data?.data?.existingId;
        if (existingId) {
          await markActionSynced(action.id, existingId);
          return { success: true, serverId: existingId, duplicate: true };
        }
      }
      
      await markActionFailed(action.id, errorMessage);
      
      // Schedule retry if retryable
      const isRetryable = !error.response || 
                          error.response.status >= 500 || 
                          error.response.status === 401;
      
      if (isRetryable && (action.retryCount || 0) < MAX_RETRIES) {
        this.scheduleRetry(action.id, (action.retryCount || 0) + 1);
      }
      
      return { success: false, error: errorMessage, retryable: isRetryable };
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Conflict Resolution
  // ─────────────────────────────────────────────────────────────────

  async resolveConflict(action, conflict) {
    console.log(`[SyncService] Resolving conflict for ${action.id}:`, conflict);

    switch (conflict.conflictType) {
      case 'DUPLICATE':
        // Server already has this record - mark as synced
        await markActionSynced(action.id, conflict.serverData?.serverId);
        return true;

      case 'MED_ALREADY_RECORDED':
        // Medication already recorded - mark as synced with server's record
        await markActionSynced(action.id, conflict.serverData?.existingRecordId);
        return true;

      case 'VERSION_MISMATCH':
        // Record was modified since offline action - keep server version
        // Mark local action as failed with conflict info
        await markActionFailed(
          action.id, 
          `Conflict: ${conflict.message}. Server data was used.`
        );
        return false;

      case 'RESOURCE_MODIFIED':
        // Resource was modified - try to merge or fail
        await markActionFailed(action.id, conflict.message);
        return false;

      default:
        // Unknown conflict type - mark as failed
        await markActionFailed(action.id, conflict.message || 'Unknown conflict');
        return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // State Persistence
  // ─────────────────────────────────────────────────────────────────

  async updateSyncState(results) {
    const now = new Date().toISOString();
    this.lastSyncedAt = now;
    
    await updateInStore(STORES.SYNC_STATE, {
      key: 'lastSync',
      timestamp: now,
      results,
      pendingCount: this.pendingCount,
      failedCount: this.failedCount,
    });
  }

  async getLastSyncInfo() {
    return await getFromStore(STORES.SYNC_STATE, 'lastSync');
  }

  // ─────────────────────────────────────────────────────────────────
  // Endpoint Mapping
  // ─────────────────────────────────────────────────────────────────

  getEndpoint(actionType) {
    const endpoints = {
      [ACTION_TYPES.CHECKIN]: {
        getUrl: () => '/mobile/shifts/check-in',
        getBody: (action) => ({
          offlineId: action.id,
          shiftId: action.payload.shiftId,
          timestamp: action.payload.timestamp || action.createdAt,
          lat: action.payload.lat,
          lng: action.payload.lng,
          notes: action.payload.notes,
        }),
      },
      [ACTION_TYPES.CHECKOUT]: {
        getUrl: () => '/mobile/shifts/check-out',
        getBody: (action) => ({
          offlineId: action.id,
          shiftId: action.payload.shiftId,
          timestamp: action.payload.timestamp || action.createdAt,
          lat: action.payload.lat,
          lng: action.payload.lng,
          notes: action.payload.notes,
        }),
      },
      [ACTION_TYPES.PROGRESS_NOTE]: {
        getUrl: () => '/mobile/progress-notes',
        getBody: (action) => ({
          offlineId: action.id,
          participantId: action.payload.participantId,
          shiftId: action.payload.shiftId,
          timestamp: action.payload.timestamp || action.createdAt,
          noteType: action.payload.noteType || 'GENERAL',
          content: action.payload.content,
          mood: action.payload.mood,
          activities: action.payload.activities,
        }),
      },
      [ACTION_TYPES.INCIDENT_REPORT]: {
        getUrl: () => '/mobile/incidents',
        getBody: (action) => ({
          offlineId: action.id,
          participantId: action.payload.participantId,
          shiftId: action.payload.shiftId,
          timestamp: action.payload.timestamp || action.createdAt,
          incidentType: action.payload.incidentType,
          severity: action.payload.severity,
          description: action.payload.description,
          location: action.payload.location,
          immediateActions: action.payload.immediateActions,
          witnesses: action.payload.witnesses,
        }),
      },
      [ACTION_TYPES.MEDICATION_LOG]: {
        getUrl: () => '/mobile/medications/log',
        getBody: (action) => ({
          clientActionId: action.id,
          medicationId: action.payload.medicationId,
          participantId: action.payload.participantId,
          dose: action.payload.dose,
          timeAdministered: action.payload.timeAdministered || action.payload.timestamp,
          status: action.payload.status,
          notes: action.payload.notes,
          scheduleId: action.payload.scheduleId,
          shiftId: action.payload.shiftId,
        }),
      },
      [ACTION_TYPES.TASK_COMPLETE]: {
        getUrl: () => '/mobile/tasks/complete',
        getBody: (action) => ({
          offlineId: action.id,
          taskId: action.payload.taskId,
          timestamp: action.payload.timestamp || action.createdAt,
          notes: action.payload.notes,
        }),
      },
      [ACTION_TYPES.HANDOVER_NOTE]: {
        getUrl: () => '/mobile/handovers',
        getBody: (action) => ({
          offlineId: action.id,
          shiftId: action.payload.shiftId,
          timestamp: action.payload.timestamp || action.createdAt,
          content: action.payload.content,
          priority: action.payload.priority,
          participantIds: action.payload.participantIds,
        }),
      },
      [ACTION_TYPES.ATTACHMENT_UPLOAD]: {
        isMultipart: true,
        getUrl: () => '/mobile/attachments',
        getBody: (action) => {
          const formData = new FormData();
          formData.append('offlineId', action.id);
          if (action.payload.file) {
            formData.append('file', action.payload.file);
          }
          formData.append('entityType', action.payload.entityType);
          formData.append('entityId', action.payload.entityId);
          return formData;
        },
      },
    };

    return endpoints[actionType];
  }

  // ─────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────

  getErrorMessage(error) {
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
    if (error.response?.data?.detail) {
      return typeof error.response.data.detail === 'string' 
        ? error.response.data.detail 
        : JSON.stringify(error.response.data.detail);
    }
    if (error.message) {
      return error.message;
    }
    return 'Unknown error occurred';
  }

  // ─────────────────────────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────────────────────────

  destroy() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    this.stopAutoSync();
    this.clearRetryTimers();
    this.listeners.clear();
    this.networkListeners.clear();
    this.isInitialized = false;
  }
}

// ─────────────────────────────────────────────────────────────────
// Singleton Instance
// ─────────────────────────────────────────────────────────────────

const syncService = new SyncService();

// ─────────────────────────────────────────────────────────────────
// Exported Functions (backward compatible with syncEngine.js)
// ─────────────────────────────────────────────────────────────────

export function initSyncService() {
  return syncService.init();
}

export function syncAllActions() {
  return syncService.syncAll();
}

export function syncAction(action) {
  return syncService.syncSingleAction(action);
}

export function isOnline() {
  return syncService.isOnline;
}

export function getSyncingStatus() {
  return syncService.isSyncing;
}

export function onSyncStatusChange(callback) {
  return syncService.onSyncStatusChange(callback);
}

export function onNetworkChange(callback) {
  return syncService.onNetworkChange(callback);
}

export function getLastSyncInfo() {
  return syncService.getLastSyncInfo();
}

export function getSyncStatus() {
  return syncService.getStatus();
}

export function retryAction(actionId) {
  return syncService.retryAction(actionId);
}

export function setupAutoSync() {
  syncService.setupAutoSync();
}

// Conflict management exports
export function getConflicts() {
  return syncService.getConflicts();
}

export function onConflictChange(callback) {
  return syncService.onConflictChange(callback);
}

export function dismissConflict(clientActionId) {
  return syncService.dismissConflict(clientActionId);
}

export function dismissAllConflicts() {
  return syncService.dismissAllConflicts();
}

// Export class for testing
export { SyncService };

// Export singleton
export default syncService;
